import type { ParsedUrlQuery } from 'node:querystring';

/**
 * Derive whether a /subscribe page load is the post-payment success screen,
 * a failed/cancelled offsite return, or neither.
 *
 * Stripe appends `redirect_status` to the configured `return_url` when the
 * user comes back from an offsite payment method (PayPal, 3DS cards, iDEAL,
 * Bancontact, …). The values aren't limited to succeeded/failed — PayPal
 * cancellations often surface as `canceled` or `requires_payment_method`,
 * and async methods like SEPA can return `processing`.
 *
 * Rules:
 *  - `redirect_status === 'succeeded'` → success
 *  - `redirect_status === 'processing'` → neither (legitimate async-pending)
 *  - any other non-empty `redirect_status` string → failure
 *  - legacy `success=true` (set by inline confirmation flows) → success,
 *    unless a failure status is also present (defensive: handles users with
 *    the pre-fix broken URL in browser history)
 */
export const derivePaymentReturnStatus = (query: ParsedUrlQuery) => {
  const redirectStatus =
    typeof query.redirect_status === 'string' ? query.redirect_status : null;

  const isPaymentFailure =
    redirectStatus !== null &&
    redirectStatus !== 'succeeded' &&
    redirectStatus !== 'processing';

  const isOnSuccessScreen =
    redirectStatus === 'succeeded' ||
    (query.success === 'true' && !isPaymentFailure);

  const isPaymentProcessing = redirectStatus === 'processing';

  return { isOnSuccessScreen, isPaymentFailure, isPaymentProcessing };
};
